"use client";

import {
    Check,
    ChevronsUpDown,
    Languages,
    LogOut,
    Settings,
    Sparkles,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { signOut } from "next-auth/react";
import { User } from "next-auth";
import {
    getDir,
    getLanguageLabel,
    languages,
    Locale,
    localeCookieName,
    t,
} from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import { useRouter } from "next/navigation";

export default function UserButton() {
    const { user } = useAuth();
    const { isMobile } = useSidebar();
    const { locale, dir } = useLocale();
    const router = useRouter();
    const withLocale = (path: string) => `/${locale}${path}`;

    const handleLocaleChange = (nextLocale: Locale) => {
        if (nextLocale === locale) return;
        document.cookie = `${localeCookieName}=${nextLocale}; Path=/; Max-Age=31536000; SameSite=Lax`;
        router.refresh();
    };

    const logOut = () => {
        signOut({ redirectTo: withLocale("/auth/login") });
    };

    if (!user) return null;

    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <DropdownMenu dir={getDir(locale)}>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton
                            size="lg"
                            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                        >
                            <UserDetails user={user} />
                            <ChevronsUpDown className="ms-auto size-4" />
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                        side={
                            isMobile
                                ? "bottom"
                                : dir === "rtl"
                                  ? "left"
                                  : "right"
                        }
                        align="end"
                        sideOffset={4}
                    >
                        <DropdownMenuLabel className="p-0 font-normal">
                            <div className="px-1 py-1.5">
                                <UserDetails user={user} />
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <a href={withLocale("/settings/pricing")}>
                            <DropdownMenuItem>
                                <Sparkles />
                                {t("user-button.dropdown.upgrade-plan")}
                            </DropdownMenuItem>
                        </a>
                        <a href={withLocale("/settings")}>
                            <DropdownMenuItem>
                                <Settings />
                                {t("user-button.dropdown.settings")}
                            </DropdownMenuItem>
                        </a>
                        <DropdownMenuSub>
                            <DropdownMenuSubTrigger
                                dir={dir}
                                className="flex flex-row items-center gap-2"
                            >
                                <Languages className="size-4" />
                                {t("languageLabel", locale)}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent alignOffset={-6}>
                                {languages.map((language) => (
                                    <DropdownMenuItem
                                        key={language.code}
                                        onSelect={() =>
                                            handleLocaleChange(language.code)
                                        }
                                    >
                                        <span className="flex-1">
                                            {getLanguageLabel(
                                                language.code,
                                                locale
                                            )}
                                        </span>
                                        {language.code === locale && (
                                            <Check className="size-4 text-muted-foreground" />
                                        )}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuSubContent>
                        </DropdownMenuSub>
                        <DropdownMenuItem onClick={logOut}>
                            <LogOut />
                            {t("user-button.dropdown.logout")}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </SidebarMenuItem>
        </SidebarMenu>
    );
}

const UserDetails = ({ user }: { user: User }) => {
    return (
        <div className="flex flex-row items-center gap-2 overflow-hidden text-start">
            <Avatar className="size-8 shrink-0 rounded-lg">
                <AvatarImage src={user.image || ""} alt={user.name || "user"} />
                <AvatarFallback className="rounded-lg">
                    {user.email?.charAt(0).toUpperCase()}
                </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-sm leading-tight">
                <span className="truncate font-semibold">{user.name}</span>
                <span className="truncate text-xs">{user.email}</span>
            </div>
        </div>
    );
};
